import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';

import type { ButtonProps } from '@capsule/consumer-design-system';
import { Button } from '@capsule/consumer-design-system';

import { useMediaQuery } from '@react-hookz/web';
import { css, useTheme } from 'styled-components';

import { DeepLinkCampaign, DeepLinkChannel, useGetDeeplinkForAppAdoption } from 'src/app/hooks/data/deeplinks';
import { useUser } from 'src/app/hooks/data/user';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import type { CourierDeliveryMethod, MailDeliveryMethod, Order } from 'src/app/types';
import { DeliveryMethodType } from 'src/consts';

import { ContrastedTitle, DeliveryTimeEstimate, StatusBlobs, StatusIcon } from '../Elements';

import { OrderStatusText } from './OrderStatusText';
import { MailSubTitle, MailTitle, OnfleetIframeContainer, OnfleetIframe } from './styles';
import styles from './styles.module.scss';

const EmbeddedMap = dynamic(() => import('src/app/components/Pages/Order/Tracking/Elements/EmbeddedMap'), {
  ssr: false,
});

interface OrderStatusProps {
  order: Order;
  shouldShowCourierMap: boolean;
}

const AppDownloadButton = ({ onClick }: Pick<ButtonProps, 'onClick'>) => {
  useSegmentEvent('viewPostOrderInlineAppDownloadCta', { shouldTrackOnMount: true });

  return (
    <Button
      onClick={onClick}
      variant="contained-secondary-lg"
      isFullWidth
      css={css`
        margin: 2rem 0;
        display: none;
        ${({ theme }) => theme.mediaQueries.smallDown} {
          display: block;
        }
      `}
    >
      Track Order In App
    </Button>
  );
};

export const TopHalfOfOrderDetails = ({ order, shouldShowCourierMap }: OrderStatusProps) => {
  const theme = useTheme();
  const isSmallDown = useMediaQuery(theme.breakpoints.smallDown);

  const trackEventSelectPostOrderAppDownload = useSegmentEvent('selectPostOrderInlineAppDownloadCta', {
    shouldTrackOnMount: false,
  });
  const { loggedInUser } = useAuth();
  const { data: user } = useUser(loggedInUser ? loggedInUser.uuid : '', Boolean(loggedInUser), 3);
  const [isUserEnabledForAppAdoptionExperiment, setIsUserEnabledForAppAdoptionExperiment] = useState<boolean>(false);
  const { fetchVariant } = useExperiments();
  useEffect(() => {
    if (!!user) {
      const evaluateAppAdoptionExperiment = async () => {
        const appAdoptionExperimentResult = await fetchVariant({
          experimentKey: 'app_adoption',
          userId: user ? user.accountId : '',
        });

        setIsUserEnabledForAppAdoptionExperiment(appAdoptionExperimentResult === 'variation_1');
      };
      evaluateAppAdoptionExperiment();
    }
  }, [fetchVariant, user]);

  const { data: deeplinkResponse } = useGetDeeplinkForAppAdoption(
    `orders/${order.id}`,
    DeepLinkCampaign.app_adoption_order_tracking,
    DeepLinkChannel.sms
  );

  const shouldShowAppAdoption = isUserEnabledForAppAdoptionExperiment && isSmallDown;

  if (!order || !order.deliveryMethod) return null;

  const isMailOrder = order?.deliveryMethod.key === DeliveryMethodType.mail;
  const isPickupStationOrder = order?.deliveryMethod.key === DeliveryMethodType.pickupStation;

  let deliveryMethodCourier = null;
  let deliveryMethodMail = null;
  let title = order.flavorText;
  if (isMailOrder) {
    deliveryMethodMail = order.deliveryMethod as MailDeliveryMethod;
    title = deliveryMethodMail?.formattedDeliveryDate;
  } else if (isPickupStationOrder) {
    deliveryMethodCourier = order.deliveryMethod as CourierDeliveryMethod;
  } else {
    deliveryMethodCourier = order.deliveryMethod as CourierDeliveryMethod;
  }

  const deliveryWindow = deliveryMethodCourier?.deliveryWindow;
  const deliveryAddress = order.deliveryAddress;

  const trackingUrl = deliveryMethodCourier?.courier?.trackingURL;

  const handleOnAppDownloadClicked = () => {
    trackEventSelectPostOrderAppDownload();
    if (deeplinkResponse?.url) {
      window.open(deeplinkResponse?.url, '_self');
    }
  };

  return (
    <>
      <div className={styles.mainContainer} aria-live="polite">
        <div className={styles.welcomeWrapper}>
          {isMailOrder ? (
            <>
              <MailSubTitle variant="h4">{order.flavorText}</MailSubTitle>
              <MailTitle variant="h1">{title}</MailTitle>
            </>
          ) : (
            <ContrastedTitle title={title} inOrderSummary={false} />
          )}
          <DeliveryTimeEstimate
            orderStatus={order.status}
            orderStatusLastUpdated={order.statusLastUpdated}
            isDeliveryDelayed={deliveryMethodCourier?.isDeliveryDelayed}
            deliveryHourRange={deliveryWindow?.formattedHoursRange}
            deliveryStartMonthDay={deliveryWindow?.formattedStartDate}
            deliveryMethod={order?.deliveryMethod}
          />
        </div>
        <StatusBlobs
          currentStep={order?.stepNum?.currentStep}
          maxSteps={order?.stepNum?.maxSteps}
          isShowing={!isMailOrder}
        />
        <OrderStatusText statusText={order.statusText} />
      </div>
      {shouldShowCourierMap ? (
        <>
          {trackingUrl ? (
            <OnfleetIframeContainer>
              <OnfleetIframe src={trackingUrl} width="425px" height="400px" title="Onfleet Courier Tracking" />
            </OnfleetIframeContainer>
          ) : (
            <EmbeddedMap courier={deliveryMethodCourier?.courier} deliveryAddress={deliveryAddress} />
          )}
        </>
      ) : (
        <StatusIcon orderStatus={order.status} deliveryMethodType={order?.deliveryMethod.key} />
      )}

      {shouldShowAppAdoption && <AppDownloadButton onClick={handleOnAppDownloadClicked} />}
    </>
  );
};
